/*
 * ------------------------------------------------------------------
 * Theme Sauce - buzzwoo E-Com Kitchen
 * bw-iv-usp-autoscroll.js | Autoscroll fuer Ignite-Slider
 * ------------------------------------------------------------------
 * Laesst einen vorhandenen <slider-component> selbsttaetig weiterblaettern.
 * Gesteuert ueber Data-Attribute AM SLIDER SELBST:
 *   data-bw-autoscroll="true"    aktiviert
 *   data-bw-autoscroll-speed="4" Sekunden pro Schritt
 *
 * Bewusst KEIN Custom Element und KEIN Wrapper: `.slider-buttons` muss
 * direktes Geschwister des <ul class="slider"> bleiben, sonst greifen
 * Ignites Auto-Hide-Regeln nicht (component-slider.css 361-365). Ein
 * umschliessendes Element waere genau der Fehler.
 *
 * Weiterblaettern laeuft ueber Ignites eigenen Next-Button, damit Paging,
 * Scrollbar-Indikator und RTL-Behandlung unveraendert aus global.js kommen.
 *
 * ERSTER SCHRITT: sobald die Leiste in den Viewport kommt, blaettert sie
 * einmal nach FIRST_STEP_DELAY (1 s) und danach im eingestellten Takt. Ohne
 * das haette der Nutzer beim Runterscrollen erst nach den vollen `speed`
 * Sekunden gesehen, dass die Leiste ueberhaupt ein Carousel ist. Deshalb
 * laeuft der Timer als sich selbst neu planende setTimeout-Kette und nicht
 * mehr als setInterval -- nur so kann der erste Abstand ein anderer sein
 * als die folgenden.
 *
 * WARUM DER RUECKSPRUNG HIER LIEGT: Ignites `SliderComponent.setSlide()`
 * kann zwar umbrechen, prueft dafuer aber `currentPage === sliderItemsToShow
 * .length` (global.js 2273). `initPages()` rechnet jedoch
 * `totalPages = sliderItemsToShow.length - slidesPerPage + 1` (global.js
 * 2016) -- bei 4 Karten und 2 pro Ansicht ist totalPages 3, currentPage
 * erreicht die 4 also nie und der eingebaute Umbruch feuert nicht. Bei
 * einem Slider mit mehr als einer sichtbaren Karte muss der Ruecksprung
 * deshalb von aussen kommen.
 * ------------------------------------------------------------------
 */
(() => {
  'use strict';

  const SELECTOR = 'slider-component[data-bw-autoscroll="true"]';

  // Abstand des ERSTEN Schritts, nachdem die Leiste sichtbar geworden ist.
  // Bewusst kurz und bewusst nicht der eingestellte Takt: er ist der Hinweis
  // "hier laesst sich wischen", nicht Teil der Laufgeschwindigkeit.
  const FIRST_STEP_DELAY = 1000;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class BwAutoscroll {
    constructor(root) {
      this.root = root;
      this.slider = root.querySelector('[id^="Slider-"]');
      this.nextButton = root.querySelector('button[name="next"]');

      // Ohne Slider-Markup oder ohne Next-Button gibt es nichts zu steuern
      // (mobile_layout: grid rendert beides nicht).
      if (!this.slider || !this.nextButton) return;

      const seconds = parseFloat(root.dataset.bwAutoscrollSpeed);
      this.delay = (Number.isFinite(seconds) && seconds > 0 ? seconds : 4) * 1000;

      this.timer = null;
      this.inViewport = false;
      this.blocked = new Set();

      // Solange false, wartet der naechste geplante Schritt nur
      // FIRST_STEP_DELAY statt this.delay. Wird beim ersten Feuern gesetzt
      // und danach nie zurueckgenommen -- der Hinweis ist einmalig, sonst
      // wuerde jedes Wieder-Eintreten in den Viewport (Scroll-Zittern um die
      // 0.2-Schwelle) erneut einen Schnellschritt ausloesen.
      this.firstStepDone = false;

      this.tick = this.tick.bind(this);
      this.sync = this.sync.bind(this);

      this.bindEvents();
      this.observe();
      this.sync();
    }

    /* --- Sperren: jeder Grund haelt den Timer unabhaengig von den anderen --- */
    block(reason) {
      this.blocked.add(reason);
      this.sync();
    }

    unblock(reason) {
      this.blocked.delete(reason);
      this.sync();
    }

    sync() {
      const mayRun =
        this.inViewport &&
        this.blocked.size === 0 &&
        !reducedMotion.matches &&
        !document.hidden;

      if (mayRun) {
        if (this.timer === null) {
          const wait = this.firstStepDone ? this.delay : FIRST_STEP_DELAY;
          this.timer = setTimeout(this.tick, wait);
        }
      } else if (this.timer !== null) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    }

    bindEvents() {
      // Zeigerbedienung: pausieren, solange jemand die Leiste benutzt.
      // Hover deckt Desktop ab, touchstart/-end den mobilen Fall -- ohne den
      // wuerde der Autoscroll gegen die Wischgeste des Nutzers anlaufen.
      //
      // Hover NUR bei echtem Zeigergeraet: auf Touch feuert `mouseenter`
      // beim Tippen emuliert, `mouseleave` dagegen erst beim Tippen woanders
      // hin -- der Autoscroll bliebe nach der ersten Beruehrung dauerhaft
      // gesperrt. Da dieses Carousel ausschliesslich mobil existiert, waere
      // das nicht der Randfall, sondern der Normalfall.
      if (window.matchMedia('(hover: hover)').matches) {
        this.root.addEventListener('mouseenter', () => this.block('hover'));
        this.root.addEventListener('mouseleave', () => this.unblock('hover'));
      }
      this.root.addEventListener('focusin', () => this.block('focus'));
      this.root.addEventListener('focusout', () => this.unblock('focus'));
      this.root.addEventListener('touchstart', () => this.block('touch'), { passive: true });
      this.root.addEventListener('touchend', () => this.unblock('touch'), { passive: true });
      this.root.addEventListener('touchcancel', () => this.unblock('touch'), { passive: true });

      document.addEventListener('visibilitychange', this.sync);
      reducedMotion.addEventListener('change', this.sync);

      // Im Theme-Editor darf die Leiste nicht unter dem PM wegscrollen,
      // waehrend er einen Block bearbeitet (Ignites eigenes Muster,
      // global.js 2296).
      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', (event) => {
          if (this.root.contains(event.target)) this.block('editor');
        });
        document.addEventListener('shopify:block:deselect', (event) => {
          if (this.root.contains(event.target)) this.unblock('editor');
        });
      }
    }

    observe() {
      if (!('IntersectionObserver' in window)) {
        this.inViewport = true;
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            this.inViewport = entry.isIntersecting;
          }
          this.sync();
        },
        { threshold: 0.2 }
      );

      observer.observe(this.root);
    }

    tick() {
      // Der Timer ist mit dem Feuern verbraucht; erst danach darf sync() am
      // Ende wieder einen planen.
      this.timer = null;

      // Auch wenn der Ueberlauf-Guard unten abbricht: der Schnellschritt gilt
      // als aufgebraucht. Sonst liefe der Timer auf Desktop -- wo es dauerhaft
      // keinen Ueberlauf gibt -- im Sekundentakt statt im eingestellten Takt.
      this.firstStepDone = true;

      // Kein horizontaler Ueberlauf = kein aktives Carousel (Desktop faellt
      // auf das Grid zurueck, dort ist der Next-Button dauerhaft disabled).
      // Ohne diese Pruefung liefe der Timer dort ins Leere und wuerde
      // endlos an den Anfang zurueckspringen.
      if (this.slider.scrollWidth > this.slider.clientWidth + 1) {
        if (this.nextButton.hasAttribute('disabled')) {
          // Am Ende angekommen -> zurueck an den Anfang. In modernen Browsern
          // ist scrollLeft 0 auch im RTL-Modus der Startrand.
          this.slider.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          this.nextButton.click();
        }
      }

      // Naechsten Schritt planen -- ueber sync(), damit eine zwischenzeitlich
      // gesetzte Sperre (Hover, Beruehrung, Tab im Hintergrund) sofort greift
      // und der Timer gar nicht erst wieder anlaeuft.
      this.sync();
    }
  }

  const start = () => {
    document.querySelectorAll(SELECTOR).forEach((root) => new BwAutoscroll(root));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
